import React, { useEffect, useState } from 'react';
import { FiPackage, FiHome, FiPlus, FiMinus, FiRefreshCw, FiAlertTriangle, FiTrendingDown, FiX, FiSearch, FiArrowRight } from 'react-icons/fi';

const API = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:5056/api' : '/api';

const fmt = (n: any) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(n) || 0);

const auth = () => {
    const t = localStorage.getItem('sp_token');
    return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const Stocks: React.FC = () => {
    const [view, setView] = useState<'tableau' | 'detail' | 'mouvements'>('tableau');
    const [tableau, setTableau] = useState<any>(null);
    const [stocks, setStocks] = useState<any[]>([]);
    const [recap, setRecap] = useState<any[]>([]);
    const [recapFormats, setRecapFormats] = useState<any[]>([]);
    const [mouvements, setMouvements] = useState<any[]>([]);
    const [depots, setDepots] = useState<any[]>([]);
    const [marques, setMarques] = useState<any[]>([]);
    const [formats, setFormats] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const [filterDepot, setFilterDepot] = useState('');
    const [filterFormat, setFilterFormat] = useState('');
    const [filterMarque, setFilterMarque] = useState('');
    const [sousSeuil, setSousSeuil] = useState(false);
    const [search, setSearch] = useState('');

    const [movOpen, setMovOpen] = useState(false);
    const [movForm, setMovForm] = useState({ depot_id: '', produit_id: '', type_mouvement: 'entree', quantite: '', motif: '', depot_lie_id: '', reference: '', notes: '' });
    const [produits, setProduits] = useState<any[]>([]);

    const loadAll = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filterDepot) params.set('depot_id', filterDepot);
        if (filterFormat) params.set('format_id', filterFormat);
        if (filterMarque) params.set('marque_id', filterMarque);
        if (sousSeuil) params.set('sous_seuil', '1');

        Promise.all([
            fetch(`${API}/stocks?${params}`, { headers: auth() }).then(r => r.json()),
            fetch(`${API}/stocks/tableau`, { headers: auth() }).then(r => r.json()),
            fetch(`${API}/stocks/depots`, { headers: auth() }).then(r => r.json()),
            fetch(`${API}/stocks/marques`, { headers: auth() }).then(r => r.json()),
            fetch(`${API}/stocks/formats`, { headers: auth() }).then(r => r.json()),
            fetch(`${API}/stocks/mouvements?limit=50`, { headers: auth() }).then(r => r.json()),
            fetch(`${API}/produits`, { headers: auth() }).then(r => r.json()),
        ]).then(([sRes, tRes, dRes, mRes, fRes, movRes, pRes]) => {
            setStocks(sRes.data || []);
            setRecap(sRes.recap_depots || []);
            setRecapFormats(sRes.recap_formats || []);
            setTableau(tRes);
            setDepots(dRes.data || []);
            setMarques(mRes.data || []);
            setFormats(fRes.data || []);
            setMouvements(movRes.data || []);
            setProduits(pRes.data || pRes || []);
        }).finally(() => setLoading(false));
    };

    useEffect(() => { loadAll(); }, [filterDepot, filterFormat, filterMarque, sousSeuil]);

    const saveMouvement = async () => {
        if (!movForm.depot_id || !movForm.produit_id || !movForm.quantite) {
            return alert('Dépôt, produit et quantité requis');
        }
        try {
            const r = await fetch(`${API}/stocks/mouvements`, {
                method: 'POST', headers: auth(),
                body: JSON.stringify({
                    ...movForm,
                    depot_id: parseInt(movForm.depot_id),
                    produit_id: parseInt(movForm.produit_id),
                    quantite: parseFloat(movForm.quantite),
                    depot_lie_id: movForm.depot_lie_id ? parseInt(movForm.depot_lie_id) : null
                })
            });
            const d = await r.json();
            if (!r.ok) return alert(d.error || 'Erreur');
            alert(`Mouvement enregistré. Stock: ${d.stock_avant} → ${d.stock_apres}`);
            setMovOpen(false);
            setMovForm({ depot_id: '', produit_id: '', type_mouvement: 'entree', quantite: '', motif: '', depot_lie_id: '', reference: '', notes: '' });
            loadAll();
        } catch (e: any) { alert(e.message); }
    };

    return (
        <div style={{ padding: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FiPackage /> Suivi des stocks
                    </h1>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748B' }}>
                        {depots.length} dépôts · {formats.length} formats · {marques.length} marques · Distribution SAMAO
                    </p>
                </div>
                <button onClick={() => setMovOpen(true)}
                    style={{ padding: '10px 18px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiPlus /> Nouveau mouvement
                </button>
            </div>

            {/* KPI by depot */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
                {recap.map(d => (
                    <div key={d.id} style={{ padding: 14, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#1E293B' }}>
                            <FiHome color="#3B82F6" /> {d.nom}
                            {d.alertes > 0 && <span style={{ marginLeft: 'auto', padding: '2px 6px', background: '#FEE2E2', color: '#EF4444', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                                <FiAlertTriangle size={10} style={{ verticalAlign: 'middle' }} /> {d.alertes}
                            </span>}
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{fmt(d.total_unites)}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{d.nb_produits} produits · {fmt(d.valeur_pa)} FCFA</div>
                    </div>
                ))}
            </div>

            {/* View tabs */}
            <div style={{ display: 'flex', gap: 4, background: '#F1F5F9', padding: 4, borderRadius: 8, marginBottom: 14, width: 'fit-content' }}>
                {[
                    { k: 'tableau', l: 'Tableau récap' },
                    { k: 'detail', l: 'Détail produit' },
                    { k: 'mouvements', l: 'Historique' }
                ].map(v => (
                    <button key={v.k} onClick={() => setView(v.k as any)}
                        style={{
                            padding: '7px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
                            background: view === v.k ? '#fff' : 'transparent',
                            color: view === v.k ? '#16A34A' : '#64748B', fontWeight: 600, fontSize: 13,
                            boxShadow: view === v.k ? '0 1px 3px rgba(0,0,0,0.06)' : 'none'
                        }}>{v.l}</button>
                ))}
            </div>

            {/* TABLEAU */}
            {view === 'tableau' && tableau && (
                <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 18, overflowX: 'auto' }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Stock par dépôt × format (vue Excel)</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
                        <thead>
                            <tr style={{ background: '#F8FAFC' }}>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Dépôt</th>
                                {tableau.formats.map((f: string) => (
                                    <th key={f} style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>{f}</th>
                                ))}
                                <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(tableau.depots).map(([nom, d]: [string, any]) => (
                                <tr key={d._id} style={{ borderTop: '1px solid #F1F5F9' }}>
                                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{nom}</td>
                                    {tableau.formats.map((f: string) => (
                                        <td key={f} style={{ padding: '10px 12px', textAlign: 'right' }}>
                                            {d[f] ? fmt(d[f]) : '—'}
                                        </td>
                                    ))}
                                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#1E293B' }}>{fmt(d._total)}</td>
                                </tr>
                            ))}
                            <tr style={{ borderTop: '2px solid #1E293B', background: '#F8FAFC' }}>
                                <td style={{ padding: '12px', fontWeight: 800 }}>TOTAL</td>
                                {tableau.formats.map((f: string) => (
                                    <td key={f} style={{ padding: '12px', textAlign: 'right', fontWeight: 800 }}>{fmt(tableau.totaux[f])}</td>
                                ))}
                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 800, color: '#16A34A' }}>{fmt(tableau.totaux._total)}</td>
                            </tr>
                            <tr style={{ borderTop: '1px solid #F1F5F9', background: '#FFFBEB' }}>
                                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#92400E' }}>Prix d'achat moyen</td>
                                {tableau.formats.map((f: string) => (
                                    <td key={f} style={{ padding: '10px 12px', textAlign: 'right', color: '#92400E' }}>
                                        {tableau.prix_achat_moyens[f] ? fmt(tableau.prix_achat_moyens[f]) : '—'}
                                    </td>
                                ))}
                                <td></td>
                            </tr>
                            <tr style={{ borderTop: '1px solid #F1F5F9', background: '#DCFCE7' }}>
                                <td style={{ padding: '10px 12px', fontWeight: 700, color: '#166534' }}>Valeur stock (PT)</td>
                                {tableau.formats.map((f: string) => {
                                    const val = (tableau.totaux[f] || 0) * (tableau.prix_achat_moyens[f] || 0);
                                    return <td key={f} style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#166534' }}>{fmt(val)}</td>;
                                })}
                                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, color: '#166534' }}>
                                    {fmt(tableau.formats.reduce((s: number, f: string) => s + (tableau.totaux[f] || 0) * (tableau.prix_achat_moyens[f] || 0), 0))} F
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {/* DETAIL */}
            {view === 'detail' && (
                <div>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                        <select value={filterDepot} onChange={e => setFilterDepot(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #E2E8F0' }}>
                            <option value="">Tous dépôts</option>
                            {depots.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                        </select>
                        <select value={filterFormat} onChange={e => setFilterFormat(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #E2E8F0' }}>
                            <option value="">Tous formats</option>
                            {formats.map(f => <option key={f.id} value={f.id}>{f.libelle}</option>)}
                        </select>
                        <select value={filterMarque} onChange={e => setFilterMarque(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #E2E8F0' }}>
                            <option value="">Toutes marques</option>
                            {marques.map(m => <option key={m.id} value={m.id}>{m.nom}</option>)}
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: sousSeuil ? '#FEE2E2' : '#fff', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                            <input type="checkbox" checked={sousSeuil} onChange={e => setSousSeuil(e.target.checked)} />
                            <FiAlertTriangle size={14} /> Sous seuil
                        </label>
                    </div>
                    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#F8FAFC' }}>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Dépôt</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Produit</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Marque</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Format</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Stock</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Seuil</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>PA</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Valeur</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Chargement…</td></tr>}
                                {!loading && stocks.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Aucun stock</td></tr>}
                                {stocks.map(s => {
                                    const alert = Number(s.quantite_actuelle) <= Number(s.seuil_alerte);
                                    return (
                                        <tr key={s.id} style={{ borderTop: '1px solid #F1F5F9', background: alert ? '#FEF2F2' : '#fff' }}>
                                            <td style={{ padding: '10px 12px', fontWeight: 600 }}>{s.depot_nom}</td>
                                            <td style={{ padding: '10px 12px' }}>{s.produit_nom}</td>
                                            <td style={{ padding: '10px 12px' }}>
                                                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                                                    background: (s.marque_couleur || '#94A3B8') + '22', color: s.marque_couleur || '#94A3B8' }}>
                                                    {s.marque_nom}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px 12px' }}>{s.format_libelle}</td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: alert ? '#EF4444' : '#1E293B' }}>
                                                {alert && <FiAlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                                                {fmt(s.quantite_actuelle)}
                                            </td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#94A3B8' }}>{fmt(s.seuil_alerte)}</td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmt(s.prix_achat)} F</td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{fmt(Number(s.quantite_actuelle) * Number(s.prix_achat))} F</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* MOUVEMENTS */}
            {view === 'mouvements' && (
                <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#F8FAFC' }}>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Date</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Type</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Dépôt</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Produit</th>
                                <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Quantité</th>
                                <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Avant → Après</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Motif</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748B', textTransform: 'uppercase' }}>Par</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mouvements.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Aucun mouvement</td></tr>}
                            {mouvements.map(m => {
                                const typeColors: any = { entree: '#16A34A', sortie: '#EF4444', transfert_in: '#3B82F6', transfert_out: '#8B5CF6', ajustement: '#F59E0B', perte: '#7C2D12' };
                                const isIn = ['entree','transfert_in'].includes(m.type_mouvement);
                                return (
                                    <tr key={m.id} style={{ borderTop: '1px solid #F1F5F9' }}>
                                        <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748B' }}>{new Date(m.date_mouvement).toLocaleString('fr-FR')}</td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                                                background: typeColors[m.type_mouvement] + '22', color: typeColors[m.type_mouvement] }}>
                                                {m.type_mouvement}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                                            {m.depot_nom}
                                            {m.depot_lie_nom && <span style={{ color: '#94A3B8', fontSize: 11 }}> ↔ {m.depot_lie_nom}</span>}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>{m.produit_nom}</td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: isIn ? '#16A34A' : '#EF4444' }}>
                                            {isIn ? '+' : '-'}{fmt(m.quantite)}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: '#94A3B8' }}>
                                            {fmt(m.stock_avant)} → <strong style={{ color: '#1E293B' }}>{fmt(m.stock_apres)}</strong>
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748B' }}>{m.motif || '—'}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748B' }}>{m.user_nom || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MOUVEMENT MODAL */}
            {movOpen && (
                <div onClick={() => setMovOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 540, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0 }}>Nouveau mouvement de stock</h3>
                            <button onClick={() => setMovOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX /></button>
                        </div>
                        <div style={{ padding: 20 }}>
                            <Label>Type</Label>
                            <select value={movForm.type_mouvement} onChange={e => setMovForm({ ...movForm, type_mouvement: e.target.value })} style={inp}>
                                <option value="entree">Entrée (réception)</option>
                                <option value="sortie">Sortie</option>
                                <option value="transfert_out">Transfert vers autre dépôt</option>
                                <option value="ajustement">Ajustement (inventaire)</option>
                                <option value="perte">Perte / casse</option>
                            </select>

                            <Label>Dépôt source</Label>
                            <select value={movForm.depot_id} onChange={e => setMovForm({ ...movForm, depot_id: e.target.value })} style={inp}>
                                <option value="">— Choisir —</option>
                                {depots.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                            </select>

                            {movForm.type_mouvement === 'transfert_out' && (
                                <>
                                    <Label>Dépôt destination</Label>
                                    <select value={movForm.depot_lie_id} onChange={e => setMovForm({ ...movForm, depot_lie_id: e.target.value })} style={inp}>
                                        <option value="">— Choisir —</option>
                                        {depots.filter(d => String(d.id) !== movForm.depot_id).map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                                    </select>
                                </>
                            )}

                            <Label>Produit</Label>
                            <select value={movForm.produit_id} onChange={e => setMovForm({ ...movForm, produit_id: e.target.value })} style={inp}>
                                <option value="">— Choisir —</option>
                                {produits.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                            </select>

                            <Label>Quantité {movForm.type_mouvement === 'ajustement' ? '(stock cible)' : ''}</Label>
                            <input type="number" min="0" step="0.01" value={movForm.quantite} onChange={e => setMovForm({ ...movForm, quantite: e.target.value })} style={inp} />

                            <Label>Référence (BL, facture…)</Label>
                            <input type="text" value={movForm.reference} onChange={e => setMovForm({ ...movForm, reference: e.target.value })} style={inp} placeholder="Optionnel" />

                            <Label>Motif</Label>
                            <input type="text" value={movForm.motif} onChange={e => setMovForm({ ...movForm, motif: e.target.value })} style={inp} placeholder="Ex: réception fournisseur SEO" />

                            <Label>Notes</Label>
                            <textarea value={movForm.notes} onChange={e => setMovForm({ ...movForm, notes: e.target.value })} style={{ ...inp, minHeight: 60 }} />
                        </div>
                        <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={() => setMovOpen(false)} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
                            <button onClick={saveMouvement} style={{ padding: '8px 18px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Enregistrer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Label: React.FC<any> = ({ children }) => (
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', marginTop: 12, marginBottom: 4 }}>{children}</label>
);

const inp: React.CSSProperties = {
    width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box'
};

export default Stocks;
